import { useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { Entry } from '@weave/schema-gen-ts/dist/schemas/comm/history/v1/history_service.pb';
import { CallStatus } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { PersonHistoryApi } from '@frontend/api-person-history';
import { PanelEvents, useCallInsightController } from '@frontend/call-insight-panel';
import { useTranslation } from '@frontend/i18n';
import { useLocalizedInfiniteQuery } from '@frontend/location-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { SpinningLoader, Text } from '@frontend/design-system';
import GroupedInfiniteList from './grouped-infinite-list';
import { HistoryEmptyStateGraphic } from './history-empty-state-graphic';
import { HistoryEntry } from './history-entry';
import { HistoryGroupHeader } from './history-group-header';

const LIMIT = 25;

export const DefaultHistoryPanel = ({ personId }: { personId: string }) => {
  const { selectedLocationIds } = useAppScopeStore();
  const {
    open: openCallInsightPanel,
    setCallList,
    setIsDemoAccount,
    activeCall,
    onEvent,
    offEvent,
  } = useCallInsightController({ source: 'callHistoryContactPanel' });
  const queryClient = useQueryClient();

  const selectedCall = activeCall?.callIntelInsights;

  const infiniteQueryProps = useLocalizedInfiniteQuery({
    queryKey: ['personHistory', personId],
    queryFn: async ({ queryKey, pageParam }) => {
      const res = await PersonHistoryApi.getPersonHistory({
        personId,
        locationId: queryKey[0],
        groupIds: selectedLocationIds,
        skip: pageParam,
        limit: LIMIT,
        includeCallIntelligence: true,
      });
      return { rows: res.entries, nextSkip: res.nextSkip };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.rows && lastPage?.rows?.length < LIMIT) return undefined;
      return lastPage.nextSkip;
    },
  });

  const hasNoHistory = infiniteQueryProps?.data?.pages?.[0].rows?.length === 0;

  const handleUpdateCallAnalysis = useCallback(
    ({ call }: PanelEvents['callAnalysisUpdated']) => {
      queryClient.setQueryData<{
        pages: { rows: Entry[]; nextSkip?: number }[];
        pageParams: any[];
      }>([...selectedLocationIds, 'personHistory', personId], (prevData) => {
        if (!prevData) return { pages: [], pageParams: [] };
        return {
          ...prevData,
          pages: prevData.pages.map((pg) => ({
            ...pg,
            rows: pg.rows.map((entry) =>
              entry.callIntelInsights?.id === call.id ? { ...entry, callIntelInsights: call } : entry
            ),
          })),
        };
      });
    },
    [personId, queryClient, selectedLocationIds]
  );

  useEffect(() => {
    setCallList(
      (infiniteQueryProps.data?.pages ?? [])
        .flatMap((page) => page.rows || [])
        .filter((row) => !!row.callIntelInsights && row.callIntelInsights.status !== CallStatus.CALL_STATUS_VOICEMAIL)
        .map(({ callIntelInsights }) => ({
          channelId: callIntelInsights?.id ?? '',
          locationId: callIntelInsights?.locationId ?? '',
          includeCallIntelligence: false,
          callIntelData: callIntelInsights,
        }))
    );
  }, [infiniteQueryProps?.data?.pages]);

  useEffect(() => {
    setIsDemoAccount(personId.startsWith('demo'));
  }, [personId, setIsDemoAccount]);

  useEffect(() => {
    onEvent('callAnalysisUpdated', handleUpdateCallAnalysis);

    return () => {
      offEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
    };
  }, [onEvent, offEvent, handleUpdateCallAnalysis]);

  if (infiniteQueryProps.isLoading) {
    return <LoadingState />;
  }

  if (hasNoHistory && !infiniteQueryProps.isLoading) {
    return <EmptyState />;
  }

  return (
    <div css={styles.wrapper}>
      <GroupedInfiniteList
        infiniteQueryProps={infiniteQueryProps}
        groupBy={(entry: Entry) => dayjs(entry.timestamp).format('MMMM YYYY')}
        renderGroupHeader={({ group, toggleGroup, ...rest }) => (
          <HistoryGroupHeader group={group} onToggle={() => toggleGroup(group)} {...rest} />
        )}
        renderItem={({ listItem }) => (
          <HistoryEntry
            kind={listItem.type === 'HISTORY_TYPE_PHONE' ? 'phone' : 'sms'}
            data={listItem}
            selectedEntryId={selectedCall?.id}
            recordId={listItem.recordId}
            onClick={() =>
              openCallInsightPanel({
                channelId: listItem.callIntelInsights?.id ?? '',
                locationId: listItem.callIntelInsights?.locationId ?? '',
                callIntelData: listItem.callIntelInsights,
                includeCallIntelligence: false,
              })
            }
          />
        )}
      />
    </div>
  );
};

const LoadingState = () => (
  <div css={{ marginTop: theme.spacing(3), textAlign: 'center' }}>
    <SpinningLoader size='medium' />
  </div>
);

const EmptyState = () => {
  const { t } = useTranslation('contacts');

  return (
    <div css={{ margin: theme.spacing(4) }}>
      <HistoryEmptyStateGraphic />
      <Text textAlign='center' css={{ marginTop: theme.spacing(3), color: theme.colors.neutral70 }}>
        {t("Looks like there's no history with this patient yet, but your story is just beginning.")}
      </Text>
    </div>
  );
};

const styles = {
  wrapper: css`
    background-color: ${theme.colors.neutral5};
  `,
};

DefaultHistoryPanel.displayName = 'DefaultHistoryPanel';
